import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Input, InputWrapper, Label, ErrorMessage } from '../components/Input';
import { Modal, ModalActions } from '../components/Modal';
import { PageSkeleton } from '../components/PageSkeleton';
import type { WorkspaceMember, WorkspaceMemberRole } from '@agency-smm/shared';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const MemberTable = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  gap: ${({ theme }) => theme.spacing.md};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-wrap: wrap;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: linear-gradient(135deg, #3B82F6, #8B5CF6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  flex-shrink: 0;
`;

const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberName = styled.div`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const MemberEmail = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const RoleBadge = styled.span<{ $role: WorkspaceMemberRole }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  background: ${({ $role }) => {
    switch ($role) {
      case 'owner': return '#8B5CF620';
      case 'admin': return '#3B82F620';
      case 'editor': return '#10B98120';
      case 'viewer': return '#6B728020';
      default: return '#6B728020';
    }
  }};
  color: ${({ $role }) => {
    switch ($role) {
      case 'owner': return '#8B5CF6';
      case 'admin': return '#3B82F6';
      case 'editor': return '#10B981';
      case 'viewer': return '#6B7280';
      default: return '#6B7280';
    }
  }};
`;

const RoleSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
`;

const MemberActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const InviteForm = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: flex-end;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
`;

function getInitials(name?: string, email?: string): string {
  if (name) {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }
  return (email || '?')[0].toUpperCase();
}

export function TeamPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceMemberRole>('viewer');
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Remove confirm
  const [removingMember, setRemovingMember] = useState<WorkspaceMember | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      const data = await apiClient.get<WorkspaceMember[]>(
        `/api/workspaces/${workspaceId}/members`
      );
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async () => {
    if (!workspaceId || !inviteEmail) return;
    setInviteError('');
    setInviteLoading(true);

    try {
      await apiClient.post(`/api/workspaces/${workspaceId}/members`, {
        email: inviteEmail,
        role: inviteRole,
      });
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('viewer');
      fetchMembers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to invite member');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    if (!workspaceId) return;
    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/members/${memberId}`, {
        role: newRole,
      });
      fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRemove = async () => {
    if (!workspaceId || !removingMember) return;
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/members/${removingMember.id}`);
      setRemovingMember(null);
      fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  return (
    <>
      <PageHeader>
        <div>
          <Title>Team Members</Title>
          <Subtitle>{members.length} member{members.length !== 1 ? 's' : ''}</Subtitle>
        </div>
        <Button onClick={() => setShowInvite(true)}>+ Invite Member</Button>
      </PageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <MemberTable>
        {members.length === 0 ? (
          <EmptyState>No team members yet. Invite someone to collaborate!</EmptyState>
        ) : (
          members.map(member => (
            <MemberRow key={member.id}>
              <Avatar>{getInitials(member.userName, member.userEmail)}</Avatar>
              <MemberInfo>
                <MemberName>{member.userName || 'Unknown'}</MemberName>
                <MemberEmail>{member.userEmail || ''}</MemberEmail>
              </MemberInfo>
              {member.role === 'owner' ? (
                <RoleBadge $role="owner">Owner</RoleBadge>
              ) : (
                <MemberActions>
                  <RoleSelect
                    value={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </RoleSelect>
                  <Button
                    $variant="outline"
                    $size="sm"
                    onClick={() => setRemovingMember(member)}
                    style={{ color: '#EF4444', borderColor: '#EF4444' }}
                  >
                    Remove
                  </Button>
                </MemberActions>
              )}
            </MemberRow>
          ))
        )}
      </MemberTable>

      {/* Invite Modal */}
      <Modal
        isOpen={showInvite}
        onClose={() => { setShowInvite(false); setInviteError(''); }}
        title="Invite Team Member"
      >
        <InviteForm>
          <InputWrapper style={{ flex: 1 }}>
            <Label>Email address</Label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="teammate@example.com"
            />
          </InputWrapper>
          <InputWrapper>
            <Label>Role</Label>
            <RoleSelect
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as WorkspaceMemberRole)}
              style={{ width: '120px', height: '40px' }}
            >
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </RoleSelect>
          </InputWrapper>
        </InviteForm>
        {inviteError && <ErrorMessage style={{ marginTop: '8px' }}>{inviteError}</ErrorMessage>}
        <ModalActions>
          <Button $variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
          <Button onClick={handleInvite} disabled={inviteLoading || !inviteEmail}>
            {inviteLoading ? 'Inviting...' : 'Send Invite'}
          </Button>
        </ModalActions>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={!!removingMember}
        onClose={() => setRemovingMember(null)}
        title="Remove Team Member"
        titleVariant="error"
        message={`Are you sure you want to remove ${removingMember?.userName || removingMember?.userEmail} from this workspace?`}
      >
        <ModalActions>
          <Button $variant="outline" onClick={() => setRemovingMember(null)}>Cancel</Button>
          <Button
            onClick={handleRemove}
            style={{ background: '#EF4444' }}
          >
            Remove
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}
